import { isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MainState } from "@app/store/state";
import { ImageInterface, ImageRevisionInterface } from "@core/interfaces/image.interface";
import { ImageApiService } from "@core/services/api/classic/images/image/image-api.service";
import { DeviceService } from "@core/services/device.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { TutorialService } from "@core/services/tutorial.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { NgbModal, NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { ConfirmationDialogComponent } from "@shared/components/misc/confirmation-dialog/confirmation-dialog.component";
import { InformationDialogComponent } from "@shared/components/misc/information-dialog/information-dialog.component";
import { TutorialFactoryService } from "@shared/components/misc/tutorial/tutorial-factory.service";
import { CookieService } from "ngx-cookie";
import { fromEvent, of, Subscription } from "rxjs";
import { debounceTime, filter, switchMap, take, takeUntil, tap } from "rxjs/operators";

import { AnnotationShapeType } from "./models/annotation-shape-type.enum";
import { Annotation } from "./models/annotation.model";
import { AnnotationService } from "./services/annotation.service";
import { CircleHandler } from "./strategies/circle-handler";
import { ShapeHandlerFactory } from "./strategies/shape-handler.factory";
import { ShapeHandler } from "./strategies/shape-handler.interface";

// Shape data type used for pending shapes and form data
interface ShapeData {
  type: AnnotationShapeType;
  color: string;
  cx?: number;
  cy?: number;
  r?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  startX?: number;
  linkUrl?: string;
  startY?: number;
  endX?: number;
  endY?: number;
  title?: string;
  message?: string;
  opacity?: number;
}

@Component({
  selector: "astrobin-annotation-tool",
  templateUrl: "./annotation-tool.component.html",
  styleUrls: ["./annotation-tool.component.scss"],
  host: {
    "[class.has-saved-annotations]": "hasSavedAnnotations",
    "[class.has-url-annotations]": "hasUrlAnnotations"
  }
})
export class AnnotationToolComponent
  extends BaseComponentDirective
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  // Make environment available to template
  @Input() active: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() imageElement: HTMLElement; // This is directly the HTML element to annotate
  @Input() windowWidth: number;
  @Input() windowHeight: number;
  @Input() setMouseOverUIElement: (value: boolean) => void;
  @Input() naturalWidth: number;
  @Input() naturalHeight: number;
  @Input() imageId: number;
  @Input() isImageOwner: boolean = false;
  @Input() revision: ImageInterface | ImageRevisionInterface;
  @Input() isRegularView: boolean = true; // Default to true - we're in regular view, not fullscreen
  @Input() hasUrlAnnotations: boolean = false; // Flag indicating if annotations were loaded from URL
  @Output() exitAnnotationMode = new EventEmitter<void>();
  @Output() annotationModeActive = new EventEmitter<boolean>(); // Emitted when annotation mode changes
  @Output() requestFullscreenMode = new EventEmitter<void>(); // Emitted when requesting fullscreen from regular viewer
  @Output() readOnlyAnnotationClick = new EventEmitter<MouseEvent>(); // Emitted when annotation is clicked in read-only mode
  @Output() annotationsSaved = new EventEmitter<string>(); // Emitted when annotations are saved with the annotations JSON
  // Make enums and types available to template
  readonly AnnotationShapeType = AnnotationShapeType;
  // Flag for save button states
  savingAnnotations: boolean = false;
  saveSuccess: boolean = false;
  // Active annotation being created/edited
  activeAnnotation: any | null = null;
  // Flag indicating if the image has saved annotations
  hasSavedAnnotations: boolean = false;
  // Mode tracking
  isDrawing: boolean = false;
  isAddingNote: boolean = false;
  // Mouse tracking
  mouseX: number | null = null;
  mouseY: number | null = null;
  // Local array to directly track annotations (with enhanced properties)
  annotations: any[] = [];
  // Properties for dragging and resizing functionality
  currentlyDragging: any | null = null;
  dragStartShapeX: number = 0;
  dragStartShapeY: number = 0;
  dragStartShapeWidth: number = 0;
  dragStartShapeHeight: number = 0;
  dragStartShapeRadius: number = 0;
  dragStartClientX: number = 0;
  dragStartClientY: number = 0;
  // Keeps track of container dimensions for calculating percentages
  containerWidth: number = 0;
  containerHeight: number = 0;
  // Determines what is being dragged
  dragMode: "whole" | "start" | "end" | "center" | "resize" | "tl" | "tr" | "bl" | "br" = "whole";
  // Minimum sizes for shapes in percentage
  readonly MIN_SIZE_PERCENT = 5;
  readonly MIN_RADIUS_PERCENT = 2;
  // Message form properties
  messageForm = new FormGroup({});
  messageFields: FormlyFieldConfig[] = [];
  messageModel: { title: string; message: string; color: string; opacity: number; linkUrl: string } = {
    title: "",
    message: "",
    color: "",
    opacity: 0.8,
    linkUrl: ""
  };
  currentAnnotationId: string | null = null;
  pendingShapeData: ShapeData | null = null;
  @ViewChild("annotationFormModal", { static: true }) annotationFormModalRef: TemplateRef<unknown>;
  @ViewChild("helpContent", { static: true }) helpContentRef: TemplateRef<unknown>;
  // Constants for reference in templates
  protected readonly Math = Math;
  // Colors for the color picker
  protected colors: string[] = [];
  // Flag to detect browser environment
  protected isBrowser: boolean;
  protected isResizing = false; // Flag to track when window is being resized
  // Image scale factor for text scaling
  protected imageScaleFactor = 1;
  // Store ResizeObserver instance for proper lifecycle management
  private _resizeObserver: ResizeObserver | null = null;
  // Flag to prevent duplicate actions when both click and touchend fire
  private _lastActionTime = 0;
  private readonly ACTION_DEBOUNCE_MS = 300;
  // Constants for magic values
  private readonly RESIZE_DEBOUNCE_MS = 300; // Debounce time for window resize events

  // Drag state flags
  private _dragInProgress = false;
  private _activePointerId: number | null = null;

  // Track if a tutorial is active
  private _tutorialActive = false;

  // Collection of subscriptions to manage all event handling
  private _eventSubscriptions: Subscription[] = [];

  // We directly use the passed imageElement from the parent component
  // Subscription for image load events
  private _imageLoadSubscription: Subscription = null;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly cookieService: CookieService,
    public readonly cdRef: ChangeDetectorRef,
    public readonly annotationService: AnnotationService,
    public readonly deviceService: DeviceService,
    public readonly utilsService: UtilsService,
    public readonly modalService: NgbModal,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly translateService: TranslateService,
    public readonly windowRefService: WindowRefService,
    public readonly imageApiService: ImageApiService,
    public readonly elementRef: ElementRef,
    public readonly tutorialService: TutorialService,
    public readonly tutorialFactoryService: TutorialFactoryService,
    @Inject(PLATFORM_ID) public readonly platformId: Object
  ) {
    super(store$);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Emit annotation mode active status whenever it changes
    if (changes.active) {
      this.annotationModeActive.emit(this.active);

      // Add or remove a class on the body element to hide UI elements
      if (this.isBrowser && typeof document !== "undefined") {
        if (this.active) {
          document.body.classList.add("annotation-mode-active");

          // If we're entering annotation mode and we have a revision with annotations, load them
          if (this.revision && this.revision.annotations && this.revision.annotations.trim() !== "") {
            this._loadSavedAnnotations();
          }

          // Only check for tutorial in fullscreen view and not in readOnly mode
          if (!this.isRegularView && !this.readOnly) {
            // Start the annotation tool toolbar tutorial if not completed already
            this._maybeStartAnnotationTutorial();
          }
        } else {
          document.body.classList.remove("annotation-mode-active");

          // End any active tutorial if component is deactivated
          if (this._tutorialActive) {
            this.tutorialService.endTutorial(false);
            this._tutorialActive = false;
          }
        }
      }
    }

    // Handle URL annotations input changes
    if (changes.hasUrlAnnotations && changes.hasUrlAnnotations.currentValue) {
      // Force visibility and ensure the component knows we're in URL annotation mode
      if (this.elementRef && this.elementRef.nativeElement) {
        this.elementRef.nativeElement.classList.add("has-url-annotations");
      }

      // Force a change detection cycle
      this.cdRef.markForCheck();
    }

    // If imageElement changed, set up the load listener again
    if (changes.imageElement && this.imageElement) {
      this._setupImageLoadListener();
      // Update the container size with the new image element
      this._updateAnnotationContainerSize();
    }

    // Check for saved annotations when revision changes
    if (changes.revision && this.revision) {
      // Check if the revision has annotations
      try {
        const annotations = this.revision.annotations ? JSON.parse(this.revision.annotations) : [];
        this.hasSavedAnnotations = Array.isArray(annotations) && annotations.length > 0;

        // If the revision has annotations, and we're in annotation mode, load them
        // BUT only if there are no URL annotations (URL annotations should trump saved annotations)
        if (
          this.active &&
          this.revision.annotations &&
          this.revision.annotations.trim() !== "" &&
          !this.hasUrlAnnotations
        ) {
          // Load the annotations from the updated revision
          this._loadSavedAnnotations();
        }
      } catch (error) {
        console.warn("Error parsing annotations:", error);
        this.hasSavedAnnotations = false;
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Setting loading flag to false and clearing annotations
    this.annotations = [];
    this.annotationService.clearAllAnnotations();

    // Get available colors
    this.colors = this.annotationService.getColors();

    // Initialize all event streams
    if (this.isBrowser) {
      this._initializeEventStreams();
    }

    // Emit initial annotation mode active status
    this.annotationModeActive.emit(this.active);

    // Initialize body class based on initial active state
    if (this.isBrowser && typeof document !== "undefined" && this.active) {
      document.body.classList.add("annotation-mode-active");
    }

    // Subscribe to the annotations$ observable - merged two subscriptions into one
    this.annotationService.annotations$.pipe(takeUntil(this.destroyed$)).subscribe(annotations => {
      // Keep local array in sync
      this.annotations = annotations.map(annotation => {
        // Create an enhanced annotation with additional properties for internal use
        const enhancedAnnotation = annotation as any;

        // Ensure the annotation has both shape.type and top-level type property for easier access
        if (annotation.shape && annotation.shape.type && !("type" in enhancedAnnotation)) {
          enhancedAnnotation.type = annotation.shape.type;
        }

        // Ensure the annotation has both shape.color and top-level color property for easier access
        if (annotation.shape && annotation.shape.color && !("color" in enhancedAnnotation)) {
          enhancedAnnotation.color = annotation.shape.color;
        }

        return enhancedAnnotation;
      });

      // Consider any array (even empty) as a saved state
      this.hasSavedAnnotations = Array.isArray(annotations);

      this.cdRef.markForCheck();
    });

    // Check for shared annotations in the URL when component initializes
    if (this.isBrowser) {
      // IMPORTANT: Force clear all annotations first
      this.annotations = [];
      this.annotationService.clearAllAnnotations();

      // DIRECT CHECK: Get annotations param directly from current URL
      const currentUrl = new URL(this.windowRefService.nativeWindow.location.href);
      const annotationsParam = currentUrl.searchParams.get("annotations");

      // First priority: Check for annotations in URL parameter
      if (annotationsParam && annotationsParam.trim() !== "") {
        // URL parameter takes precedence over saved annotations
        try {
          // Load the annotations
          this.annotationService.loadFromUrlParam(annotationsParam);

          // Update UI after annotations are loaded
          setTimeout(() => {
            this.cdRef.markForCheck();
          }, 100);
        } catch (e) {
          // Handle error loading annotations from URL
          this.popNotificationsService.error(
            this.translateService.instant("Failed to load annotations: {{error}}", { error: e.message })
          );

          // Make sure annotations are cleared on error
          this.annotations = [];
          this.annotationService.clearAllAnnotations();
        }
      } else if (this.revision && this.revision.annotations) {
        // Second priority: If no URL annotations, load from revision object
        this._loadSavedAnnotations();
      }
    }

    // Initialize formly form with validators
    this.messageForm = new FormGroup({});
    this._initMessageFormFields();

    // Set up form validation monitoring
    this.messageForm.valueChanges.subscribe(() => {
      this.cdRef.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      // Set up the image load listener
      this._setupImageLoadListener();

      // Chain all initialization steps with proper sequence
      of(null)
        .pipe(
          switchMap(() => this.utilsService.delay(50)),
          tap(() => {
            this._updateAnnotationContainerSize();
          }),
          switchMap(() => this.utilsService.delay(100)),
          tap(() => {
            this._setupResizeObserver();

            // Also start tutorial if component is initialized with active=true
            if (this.active && !this.isRegularView && !this.readOnly) {
              this._maybeStartAnnotationTutorial();
            }
          }),
          takeUntil(this.destroyed$)
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      // Clean up any subscriptions
      if (this._imageLoadSubscription) {
        this._imageLoadSubscription.unsubscribe();
        this._imageLoadSubscription = null;
      }

      // Clean up event subscriptions
      this._cleanupEventSubscriptions();

      // Clean up the ResizeObserver
      this._cleanupResizeObserver();

      // Make sure to remove the body class when component is destroyed
      document.body.classList.remove("annotation-mode-active");

      // End any active tutorial if component is destroyed
      if (this._tutorialActive) {
        this.tutorialService.endTutorial(false);
        this._tutorialActive = false;
      }
    }

    // Call parent class ngOnDestroy - this will also complete the destroyed$ subject
    // which will clean up all our RxJS pipes using takeUntil(this.destroyed$)
    super.ngOnDestroy();
  }

  /**
   * Handle window resize events to reposition annotations
   */
  handleWindowResize(event: UIEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // Update the annotation container to match the image dimensions
    this._updateAnnotationContainerSize();

    this.cdRef.markForCheck();
  }

  /**
   * Prepare a rectangle annotation and open the form
   */
  makeRect(): void {
    this._prepareShape(AnnotationShapeType.RECTANGLE);
  }

  /**
   * Prepare a circle annotation and open the form
   */
  makeCircle(): void {
    this._prepareShape(AnnotationShapeType.CIRCLE);
  }

  /**
   * Prepare a text annotation and open the form
   */
  makeText(): void {
    this._prepareShape(AnnotationShapeType.TEXT);
  }

  makeLink(): void {
    this._prepareShape(AnnotationShapeType.LINK);
  }

  // Get the left position of an annotation
  getAnnotationLeft(ann: Annotation): number {
    const handler = this.getShapeHandler(ann);
    return handler.getLeft(ann);
  }

  // Get the top position of an annotation
  getAnnotationTop(ann: Annotation): number {
    const handler = this.getShapeHandler(ann);
    return handler.getTop(ann);
  }

  // Get the width of an annotation
  getAnnotationWidth(ann: Annotation): number {
    const handler = this.getShapeHandler(ann);
    return handler.getWidth(ann);
  }

  // Get the height of an annotation
  getAnnotationHeight(ann: Annotation): number {
    const handler = this.getShapeHandler(ann);
    return handler.getHeight(ann);
  }

  // Drawing preview helpers
  getDrawingLeft(): number {
    if (!this.activeAnnotation) {
      return 0;
    }

    // Convert active annotation shape points to the appropriate shape properties
    const tempAnnotation = this._pointsToShapeProperties(this.activeAnnotation);
    if (!tempAnnotation) {
      return 0;
    }

    // Use the shape handler to get the left position
    const handler = this.getShapeHandler(tempAnnotation);
    return handler.getLeft(tempAnnotation);
  }

  getDrawingTop(): number {
    if (!this.activeAnnotation) {
      return 0;
    }

    // Convert active annotation shape points to the appropriate shape properties
    const tempAnnotation = this._pointsToShapeProperties(this.activeAnnotation);
    if (!tempAnnotation) {
      return 0;
    }

    // Use the shape handler to get the top position
    const handler = this.getShapeHandler(tempAnnotation);
    return handler.getTop(tempAnnotation);
  }

  getDrawingWidth(): number {
    if (!this.activeAnnotation) {
      return 0;
    }

    // Convert active annotation shape points to the appropriate shape properties
    const tempAnnotation = this._pointsToShapeProperties(this.activeAnnotation);
    if (!tempAnnotation) {
      return 0;
    }

    // Use the shape handler to get the width
    const handler = this.getShapeHandler(tempAnnotation);
    return handler.getWidth(tempAnnotation);
  }

  getDrawingHeight(): number {
    if (!this.activeAnnotation) {
      return 0;
    }

    // Convert active annotation shape points to the appropriate shape properties
    const tempAnnotation = this._pointsToShapeProperties(this.activeAnnotation);
    if (!tempAnnotation) {
      return 0;
    }

    // Use the shape handler to get the height
    const handler = this.getShapeHandler(tempAnnotation);
    return handler.getHeight(tempAnnotation);
  }

  /**
   * Helper method to convert an annotation with points to one with shape-specific properties
   * @param annotation The annotation with shape.points
   * @returns An annotation with shape-specific properties (x, y, width, height or cx, cy, r)
   */
  _pointsToShapeProperties(annotation: any): any {
    if (!annotation || !annotation.shape || !annotation.shape.points || annotation.shape.points.length < 2) {
      return null;
    }

    const result: any = {
      ...annotation,
      type: annotation.shape.type
    };

    if (annotation.shape.type === AnnotationShapeType.RECTANGLE) {
      const points = annotation.shape.points;
      // For rectangle: points[0] is top-left, points[1] is bottom-right
      result.x = Math.min(points[0].x, points[1].x);
      result.y = Math.min(points[0].y, points[1].y);
      result.width = Math.abs(points[1].x - points[0].x);
      result.height = Math.abs(points[1].y - points[0].y);
    } else if (annotation.shape.type === AnnotationShapeType.CIRCLE) {
      const points = annotation.shape.points;
      // For circle: points[0] is center, points[1] determines radius
      result.cx = points[0].x;
      result.cy = points[0].y;
      result.r = Math.sqrt(Math.pow(points[1].x - points[0].x, 2) + Math.pow(points[1].y - points[0].y, 2));
    }

    return result;
  }

  // Get appropriate cursor for the current drag mode
  getCursorForDragMode(): string {
    switch (this.dragMode) {
      case "tl":
        return "nwse-resize";
      case "tr":
        return "nesw-resize";
      case "bl":
        return "nesw-resize";
      case "br":
        return "nwse-resize";
      case "resize":
        return "ns-resize";
      case "start":
      case "end":
        return "move";
      default:
        return "move";
    }
  }

  /**
   * Helper method to determine if an annotation is a rectangle
   * Used in templates for conditional rendering
   */
  isRectangle(annotation: any): boolean {
    const type = annotation.type || (annotation.shape && annotation.shape.type) || AnnotationShapeType.RECTANGLE;
    return type === AnnotationShapeType.RECTANGLE;
  }

  /**
   * Helper method to determine if an annotation is a circle
   * Used in templates for conditional rendering
   */
  isCircle(annotation: any): boolean {
    const type = annotation.type || (annotation.shape && annotation.shape.type) || AnnotationShapeType.RECTANGLE;
    return type === AnnotationShapeType.CIRCLE;
  }

  /**
   * Helper method to determine if an annotation is text
   * Used in templates for conditional rendering
   */
  isText(annotation: any): boolean {
    const type = annotation.type || (annotation.shape && annotation.shape.type) || AnnotationShapeType.RECTANGLE;
    return type === AnnotationShapeType.TEXT;
  }

  isLink(annotation: any): boolean {
    const type = annotation.type || (annotation.shape && annotation.shape.type) || AnnotationShapeType.RECTANGLE;
    return type === AnnotationShapeType.LINK;
  }

  /**
   * Helper method to get the color of an annotation
   * Handles both shape.color and top-level color properties
   */
  getColor(annotation: any): string {
    return (
      (annotation as any).color ||
      (annotation.shape && annotation.shape.color) ||
      this.annotationService.getDefaultColor()
    );
  }

  /**
   * Helper method to get the opacity of an annotation
   * Handles both shape.opacity and top-level opacity properties
   */
  getOpacity(annotation: any): number {
    if (!annotation) {
      return 0.8; // Default opacity if annotation is null or undefined
    }

    return (annotation as any).opacity !== undefined
      ? (annotation as any).opacity
      : annotation.shape && annotation.shape.opacity !== undefined
        ? annotation.shape.opacity
        : 0.8; // Default opacity
  }

  /**
   * Clear all annotations with confirmation
   */
  confirmClearAll(): void {
    // Check if there are any annotations to clear
    if (this.annotations.length === 0) {
      return; // Do nothing if there are no annotations
    }

    // Using statically imported component
    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      centered: true,
      backdrop: "static"
    });
    const componentInstance = modalRef.componentInstance;

    componentInstance.title = this.translateService.instant("Clear all annotations");
    componentInstance.message = this.translateService.instant("Are you sure you want to delete all annotations?");
    componentInstance.showAreYouSure = false;
    componentInstance.confirmLabel = this.translateService.instant("Yes, clear all");

    modalRef.result.then(
      () => {
        // User confirmed, clear all annotations
        this._clearAll();
      },
      () => {
        // User cancelled, do nothing
      }
    );
  }

  /**
   * Confirm deletion of an annotation
   */
  confirmDeleteAnnotation(id: string): void {
    const annotation = this.annotations.find(ann => ann.id === id);
    if (!annotation) {
      console.warn("Annotation not found for deletion:", id);
      return;
    }

    // Using statically imported component
    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      centered: true,
      backdrop: "static"
    });
    const componentInstance = modalRef.componentInstance;

    const title = annotation.title || this.translateService.instant("Untitled annotation");
    componentInstance.title = this.translateService.instant("Delete annotation");
    componentInstance.message = this.translateService.instant(
      'Are you sure you want to delete the annotation "{{title}}"?',
      { title }
    );
    componentInstance.showAreYouSure = false;
    componentInstance.confirmLabel = this.translateService.instant("Yes, delete");

    modalRef.result.then(
      () => {
        // User confirmed, delete the annotation
        this.deleteAnnotation(id);
      },
      () => {
        // User cancelled, do nothing
      }
    );
  }

  /**
   * Show annotation info in read-only mode
   */
  showAnnotationInfo(annotation: Annotation): void {
    if (!annotation) {
      return;
    }

    // Using statically imported component
    const modalRef = this.modalService.open(InformationDialogComponent, {
      centered: true,
      backdrop: "static"
    });

    const componentInstance = modalRef.componentInstance;

    // Use the annotation title as the dialog title
    componentInstance.title = annotation.title || this.translateService.instant("Annotation");

    // Use the message property for the description
    componentInstance.message = annotation.message || "";

    // Use the file-alt icon instead of the default info icon
    componentInstance.iconName = "file-alt";
  }

  /**
   * Unified start drag method that works with both mouse and touch via pointer events
   */
  startDrag(
    event: PointerEvent,
    annotation: Annotation,
    mode: "whole" | "start" | "end" | "center" | "resize" | "tl" | "tr" | "bl" | "br" = "whole"
  ): void {
    // Prevent default browser behavior
    event.preventDefault();
    event.stopPropagation();

    // Stop text selection
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    }

    // Store the pointer ID for tracking this specific pointer through the drag operation
    this._activePointerId = event.pointerId;

    // Set global drag in progress flag
    this._dragInProgress = true;

    // Get container dimensions once at the start of dragging
    const container = document.querySelector(".annotation-container") as HTMLElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      this.containerWidth = rect.width;
      this.containerHeight = rect.height;
    }

    // Store the current annotation being dragged and the drag mode
    this.currentlyDragging = annotation;
    this.dragMode = mode;

    // Store starting pointer position - this will be our reference point for calculating deltas
    this.dragStartClientX = event.clientX;
    this.dragStartClientY = event.clientY;

    // We need to store all possible shape properties regardless of type
    // This way we can pass a complete dragStart object to any handler
    this.dragStartShapeX = (annotation as any).x || (annotation as any).cx || 0;
    this.dragStartShapeY = (annotation as any).y || (annotation as any).cy || 0;
    this.dragStartShapeWidth = (annotation as any).width || 0;
    this.dragStartShapeHeight = (annotation as any).height || 0;
    this.dragStartShapeRadius = (annotation as any).r || 0;

    // Apply touch-specific styling for visual feedback if this is a touch event
    if (event.pointerType === "touch" && this.isBrowser && typeof document !== "undefined") {
      document.body.classList.add("annotation-touch-drag-active");
    }

    // Set pointer capture to ensure we get all events even if pointer moves outside the element
    if (event.target instanceof Element) {
      try {
        event.target.setPointerCapture(event.pointerId);
      } catch (e) {
        console.warn("Failed to set pointer capture:", e);
      }
    }

    // Force update UI
    this.cdRef.detectChanges();
  }

  /**
   * Unified drag move handler for pointer events
   */
  dragMove(event: PointerEvent): void {
    if (!this.currentlyDragging || !this._dragInProgress) {
      return;
    }

    // Verify we're handling the same pointer that started the drag
    if (event.pointerId !== this._activePointerId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Calculate pixel movement
    const deltaXPixels = event.clientX - this.dragStartClientX;
    const deltaYPixels = event.clientY - this.dragStartClientY;

    // Get container dimensions for bounds checking and percentage calculations
    const container = this.elementRef.nativeElement.querySelector(".annotation-container");
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    this.containerWidth = rect.width;
    this.containerHeight = rect.height;

    // Convert pixel movement to percentage movement based on current container size
    const deltaXPercent = (deltaXPixels / rect.width) * 100;
    const deltaYPercent = (deltaYPixels / rect.height) * 100;

    // Get the appropriate handler for the current shape
    const handler = this.getShapeHandler(this.currentlyDragging);

    // Create a dragStart object with the initial state
    const dragStart = {
      x: this.dragStartShapeX,
      y: this.dragStartShapeY,
      width: this.dragStartShapeWidth,
      height: this.dragStartShapeHeight,
      cx: this.dragStartShapeX,
      cy: this.dragStartShapeY,
      r: this.dragStartShapeRadius
    };

    // Get the minimum size constraint based on shape type
    const minSizeConstraint = handler instanceof CircleHandler ? this.MIN_RADIUS_PERCENT : this.MIN_SIZE_PERCENT;

    // Use the handler to update the shape
    const updatedShape = handler.updateOnDrag(
      this.dragMode,
      this.currentlyDragging,
      dragStart,
      deltaXPercent,
      deltaYPercent,
      minSizeConstraint
    );

    // Apply the updates to the current shape
    Object.assign(this.currentlyDragging, updatedShape);

    // Force update UI
    this.cdRef.detectChanges();
  }

  /**
   * Unified end drag method that works with both mouse and touch via pointer events
   */
  endDrag(event?: PointerEvent): void {
    if (!this.currentlyDragging) {
      return;
    }

    // Prevent default browser behavior if event is provided
    if (event) {
      event.preventDefault();
      event.stopPropagation();

      // Release pointer capture
      if (event.target instanceof Element) {
        try {
          event.target.releasePointerCapture(event.pointerId);
        } catch (e) {
          // Ignore errors with pointer capture release
        }
      }
    }

    // Get the current annotation from the local array
    const id = this.currentlyDragging.id;

    // Get the appropriate handler for the current shape
    const handler = this.getShapeHandler(this.currentlyDragging);

    // Use the handler to convert the shape to points
    const points = handler.toPoints(this.currentlyDragging);

    // Commit final state to the annotation service
    this.annotationService.updateAnnotationShape(id, {
      points: points
    });

    // Remove touch drag active class if present
    if (this.isBrowser && typeof document !== "undefined") {
      document.body.classList.remove("annotation-touch-drag-active");
    }

    // Reset dragging state
    this._dragInProgress = false;
    this.currentlyDragging = null;
    this.dragMode = "whole";
    this._activePointerId = null;

    // Force UI update
    this.cdRef.detectChanges();
  }

  /**
   * Cancel the current annotation creation
   */
  cancelAnnotation(): void {
    if (this.activeAnnotation) {
      this.annotationService.removeAnnotation(this.activeAnnotation.id);
      this.activeAnnotation = null;
    }

    this.isDrawing = false;
    this.isAddingNote = false;
    this.cdRef.markForCheck();
  }

  /**
   * Check if an annotation matches exactly what's in the saved annotations
   * @param id The ID of the annotation to check
   * @returns true only if the annotation exists exactly in the revision's saved annotations
   */
  isFromSavedAnnotations(id: string): boolean {
    // If we have no revision or annotations, return false
    if (!this.revision || !this.revision.annotations || this.revision.annotations.trim() === "") {
      return false;
    }

    try {
      // Parse saved annotations from the revision (source of truth)
      const savedAnnotations = JSON.parse(this.revision.annotations);
      if (!Array.isArray(savedAnnotations)) {
        return false;
      }

      // Direct ID comparison with the source of truth (the revision)
      return savedAnnotations.some(savedAnn => savedAnn.id === id);
    } catch (error) {
      console.warn("Error checking if annotation is from saved annotations:", error);
      return false;
    }
  }

  /**
   * Check if an annotation is from URL parameters (but not from saved annotations)
   * @param id The ID of the annotation to check
   * @returns true if the annotation is from URL parameters and not in saved annotations
   */
  isFromUrlOnly(id: string): boolean {
    // An annotation is URL-only if:
    // 1. It's in the URL annotations list (loaded from URL) AND
    // 2. It doesn't match any saved annotation in the revision
    const fromUrl = this.annotationService.urlAnnotationIds.includes(id);
    const fromSaved = this.isFromSavedAnnotations(id);
    return fromUrl && !fromSaved;
  }

  /**
   * Exit annotation mode
   */
  exitAnnotationModeHandler(): void {
    // First clean up any active drawing
    this.cancelAnnotation();

    // Save a direct reference to native window for manipulating the URL
    if (this.isBrowser) {
      try {
        const location = this.windowRefService.nativeWindow.location;
        const url = new URL(location.href);

        // Remove annotations param
        url.searchParams.delete("annotations");

        // Apply the change directly to history
        this.windowRefService.nativeWindow.history.replaceState({}, "", url.toString());

        // Remove the body class to show UI elements again
        document.body.classList.remove("annotation-mode-active");
      } catch (e) {
        console.error("Error manipulating URL:", e);
      }
    }

    // Clear the UI state
    this.annotations = [];

    // Also clear annotations in the service if we're exiting from fullscreen view
    // but keep them if we're just toggling between regular and fullscreen views
    if (!this.isRegularView) {
      this.annotationService.clearAllAnnotations();
    }

    // Mark for check to update the UI
    this.cdRef.markForCheck();

    // Emit events to notify parent components
    this.exitAnnotationMode.emit();
    this.annotationModeActive.emit(false);
  }

  /**
   * Delete an annotation
   */
  deleteAnnotation(id: string): void {
    // Use the service method to remove the annotation
    // Our local array will be updated via the subscription
    this.annotationService.removeAnnotation(id);
    this.cdRef.markForCheck();
  }

  /**
   * Open the annotation form for creating a new shape
   */
  openAnnotationForm(shapeData: ShapeData): void {
    // Store the shape data to be created when the form is submitted
    this.pendingShapeData = shapeData;

    // Set default values based on shape type
    let defaultColor = this.getColor(shapeData);
    let defaultOpacity = 0.8; // Default opacity for rectangles and circles

    // Override defaults for text annotations
    if (shapeData.type === AnnotationShapeType.TEXT) {
      defaultColor = "#000000"; // Black for text annotations
      defaultOpacity = 1.0; // Full opacity for text
    }

    // Override defaults for link annotations
    if (shapeData.type === AnnotationShapeType.LINK) {
      defaultColor = "#000000"; // Black background for link annotations
      defaultOpacity = 0.85; // Slightly higher opacity for links
    }

    // Reset the model for a new annotation with appropriate defaults
    this.messageModel = {
      title: "", // Start with empty title
      message: "", // Start with empty message
      color: defaultColor,
      opacity: defaultOpacity,
      linkUrl: ""
    };

    // Reset the form and prepare it for a new entry
    this.messageForm = new FormGroup({});

    // Clear previous fields to ensure proper reinitialization
    this.messageFields = [];

    // Initialize form fields with proper visibility based on shape type
    this._initMessageFormFields(false);

    // Force change detection to ensure form updates
    this.cdRef.detectChanges();

    // Open the modal
    const modalRef = this.modalService.open(this.annotationFormModalRef, {
      centered: true,
      backdrop: "static"
    });

    // Handle modal close (save)
    modalRef.result.then(
      result => {
        // Modal was closed with save - create the shape and save form data
        this._createAnnotationFromPendingData(result);
      },
      () => {
        // Modal was dismissed - clear pending shape
        this.pendingShapeData = null;
      }
    );
  }

  /**
   * Show the message form for an existing annotation using NgbModal
   */
  showMessageForm(id: string, modalTemplate?: TemplateRef<any>): void {
    this.currentAnnotationId = id;

    // Find the annotation
    const annotation = this.annotations.find(ann => ann.id === id);

    if (!annotation) {
      console.warn("Annotation not found:", id);
      return;
    }

    // Determine if this annotation is a text annotation (for required validation)
    const isTextAnnotation = this.isText(annotation);

    // Save the type to pendingShapeData so we can use it for placeholder logic
    // Determine the type properly for all annotation types
    let annotationType;
    if (this.isText(annotation)) {
      annotationType = AnnotationShapeType.TEXT;
    } else if (this.isLink(annotation)) {
      annotationType = AnnotationShapeType.LINK;
    } else if (this.isCircle(annotation)) {
      annotationType = AnnotationShapeType.CIRCLE;
    } else {
      annotationType = AnnotationShapeType.RECTANGLE;
    }

    this.pendingShapeData = {
      type: annotationType,
      color: this.getColor(annotation),
      linkUrl: annotation.linkUrl || ""
    };

    // Initialize the model with existing values
    this.messageModel = {
      title: annotation.title || "",
      message: annotation.message || "",
      color: this.getColor(annotation),
      opacity: this.getOpacity(annotation),
      linkUrl: annotation.linkUrl || ""
    };

    // Reset the form controls
    this.messageForm = new FormGroup({});

    // Clear previous fields to ensure proper reinitialization
    this.messageFields = [];

    // Initialize message form fields with the right validation based on annotation type
    this._initMessageFormFields(false);

    // Force change detection to ensure form updates
    this.cdRef.detectChanges();

    // Open the modal with the template reference
    if (modalTemplate) {
      const modalRef = this.modalService.open(modalTemplate, {
        centered: true,
        backdrop: "static"
      });

      // Handle modal close (save)
      modalRef.result.then(
        result => {
          // Modal was closed with save
          this.submitMessageForm(result);
        },
        () => {
          // Modal was dismissed
          this.pendingShapeData = null; // Reset pendingShapeData on dismiss
        }
      );
    }
  }

  /**
   * Validate form and close modal only if valid
   */
  validateAndCloseModal(modal: any, form: FormGroup, formData: any, event?: MouseEvent): void {
    // Check if the title is empty for a text annotation
    const isTextAnnotation = this.pendingShapeData?.type === AnnotationShapeType.TEXT;
    const isLinkAnnotation = this.pendingShapeData?.type === AnnotationShapeType.LINK;
    const titleValue = (form.get("title")?.value || "").trim();
    const linkUrlValue = (form.get("linkUrl")?.value || "").trim();

    if (isTextAnnotation && !titleValue) {
      // For text annotations, title is required - mark the field as touched to show error
      const titleControl = form.get("title");
      if (titleControl) {
        titleControl.markAsTouched();
        titleControl.setErrors({ required: true });
      }

      // Prevent form submission
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Force UI update
      this.cdRef.detectChanges();
      return;
    }

    if (isLinkAnnotation) {
      const linkUrlControl = form.get("linkUrl");

      if (!linkUrlValue) {
        // For link annotations, URL is required - mark the field as touched to show error
        if (linkUrlControl) {
          linkUrlControl.markAsTouched();
          linkUrlControl.setErrors({ required: true });
        }
        // Prevent form submission
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        // Force UI update
        this.cdRef.detectChanges();
        return;
      }

      // Validate URL has http(s) protocol
      if (!linkUrlValue.match(/^https?:\/\/.*/)) {
        if (linkUrlControl) {
          linkUrlControl.markAsTouched();
          linkUrlControl.setErrors({ pattern: true });
        }
        // Prevent form submission
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        // Force UI update
        this.cdRef.detectChanges();
        return;
      }
    }

    if (form.valid) {
      // Get values safely from the form or model to avoid null errors
      const formResult = {
        title: (form.get("title")?.value || this.messageModel.title || "").trim(),
        message: (form.get("message")?.value || this.messageModel.message || "").trim(),
        color: form.get("color")?.value || this.messageModel.color,
        opacity: this.messageModel.opacity !== undefined ? this.messageModel.opacity : 0.8,
        linkUrl: (form.get("linkUrl")?.value || this.messageModel.linkUrl || "").trim()
      };

      // Close the modal with the clean copied data
      modal.close(formResult);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        control.markAsTouched();
        control.markAsDirty();
      });

      // Prevent default button action and propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // If using formly, trigger validation on all fields
      // This will ensure validation messages are displayed immediately
      this.messageFields.forEach(field => {
        if (field.formControl) {
          field.formControl.markAsTouched();
        }
      });

      // Manually mark for check to ensure UI updates
      this.cdRef.detectChanges();
    }
  }

  /**
   * Submit the message form and save the title and message
   */
  submitMessageForm(formData: any): void {
    if (!this.currentAnnotationId) {
      return;
    }

    // Safely extract values with proper null handling
    const title = (formData?.title || "").trim();
    const messageText = (formData?.message || "").trim();
    const color = formData?.color;
    const opacity = formData?.opacity !== undefined ? formData.opacity : 0.8;

    // Always use the annotation service to update the message
    // This ensures proper ID regeneration and tracking
    const annotationIndex = this.annotations.findIndex(ann => ann.id === this.currentAnnotationId);

    if (annotationIndex !== -1) {
      // Make a complete copy of the current annotation
      const annotation = { ...this.annotations[annotationIndex] };

      // Update all properties directly
      annotation.title = title;
      annotation.message = messageText;
      annotation.linkUrl = formData?.linkUrl || "";
      (annotation as any).color = color;
      (annotation as any).opacity = opacity;

      // Update shape properties
      if (annotation.shape) {
        annotation.shape = {
          ...annotation.shape,
          color: color,
          opacity: opacity
        };
      }

      // Remove the annotation
      this.annotationService.removeAnnotation(this.currentAnnotationId);

      // After a small delay to ensure removal completes
      this.utilsService.delay(50).subscribe(() => {
        // Add the updated annotation back
        this.annotationService.createAnnotationFromExisting(annotation);

        // Force UI refresh
        this.cdRef.detectChanges();
      });
    }

    // Reset current ID and pendingShapeData
    this.currentAnnotationId = null;
    this.pendingShapeData = null;
    this.cdRef.markForCheck();
  }

  /**
   * Share the current annotations
   */
  shareAnnotations(): void {
    // Generate the URL parameter from the annotations
    let urlParam;
    try {
      // Check if there are annotations to share
      if (!this.annotations || this.annotations.length === 0) {
        // For empty annotations, create a special empty parameter
        urlParam = "[]";
      } else {
        // Generate parameter from existing annotations
        urlParam = this.annotationService.getUrlParam();
      }

      if (!urlParam) {
        console.error("Failed to generate URL parameter for annotations");
        this.popNotificationsService.error(this.translateService.instant("Failed to generate shareable URL"));
        return;
      }
    } catch (error) {
      console.error("Error generating URL parameter:", error);
      this.popNotificationsService.error(this.translateService.instant("Failed to generate shareable URL"));
      return;
    }

    // Add or update the annotations parameter without navigation
    if (this.isBrowser) {
      // Get the current URL
      const currentUrl = this.windowRefService.getCurrentUrl();

      // Update the annotations parameter
      currentUrl.searchParams.set("annotations", urlParam);

      // Create a shareable URL without the fullscreen hash (but keep other parameters)
      const shareableUrl = new URL(currentUrl.toString());
      // Only remove the fullscreen hash if it exists, leave other hashes intact
      if (shareableUrl.hash === "#fullscreen") {
        shareableUrl.hash = "";
      }

      // Update the URL without navigation but keep the current hash for user
      this.windowRefService.replaceState({}, currentUrl.toString());

      // Show information dialog with explanation - using statically imported component
      const modalRef = this.modalService.open(InformationDialogComponent, {
        size: "md",
        centered: true,
        backdrop: "static"
      });

      const componentInstance = modalRef.componentInstance;

      // Set the title and icon
      componentInstance.title = this.translateService.instant("Share annotations");
      componentInstance.iconName = "share-alt";

      // Set the explanation message
      let message = this.translateService.instant(
        "The link now includes your annotations. Just share it if you want others to see them."
      );

      // Add note about link-based annotations
      message +=
        "\n\n" +
        this.translateService.instant(
          "Heads up: The annotations aren't saved anywhere - only this exact link shows them."
        );

      // Add extra note for image owners about saving
      if (this.isImageOwner && this.imageId) {
        message +=
          "\n\n" +
          this.translateService.instant(
            "As the image owner, you can use the Save button to permanently add these annotations to the image."
          );
      }

      // Set the message
      componentInstance.message = message;

      // Add custom buttons
      componentInstance.buttons = [
        {
          label: this.translateService.instant("Copy Link"),
          class: "btn-primary",
          callback: () => {
            this.windowRefService
              .copyToClipboard(shareableUrl.toString())
              .then(success => {
                if (success) {
                  this.popNotificationsService.success(
                    this.translateService.instant("Link with annotations copied to clipboard")
                  );
                } else {
                  this.popNotificationsService.info(
                    this.translateService.instant("Please copy the URL from your browser's address bar")
                  );
                }
              })
              .catch(error => {
                console.error("Error copying to clipboard:", error);
                this.popNotificationsService.info(
                  this.translateService.instant("Please copy the URL from your browser's address bar")
                );
              });
            modalRef.close();
          }
        },
        {
          label: this.translateService.instant("Close"),
          class: "btn-outline-secondary",
          callback: () => {
            modalRef.close();
          }
        }
      ];
    }
  }

  /**
   * Show help instructions in an offcanvas panel
   */
  showHelp(): void {
    if (!this.isBrowser) {
      return;
    }

    // Open the offcanvas using the template reference
    this.offcanvasService.open(this.helpContentRef, {
      position: this.deviceService.offcanvasPosition(),
      panelClass: "image-viewer-offcanvas help-offcanvas",
      backdropClass: "image-viewer-offcanvas-backdrop"
    });
  }

  /**
   * Save annotations to the database (owner only)
   */
  saveAnnotations(): void {
    if (!this.isImageOwner || !this.revision || !this.revision.pk) {
      return;
    }

    // Check if the image already has saved annotations
    const alreadyHasAnnotations = this.hasSavedAnnotations;

    if (alreadyHasAnnotations) {
      // If the image already has annotations, skip the confirmation and proceed directly
      this._performSaveAnnotations();
    } else {
      // Only show confirmation dialog for first-time annotation saves
      // Import dynamically to avoid circular dependency
      void import("@shared/components/misc/confirmation-dialog/confirmation-dialog.component").then(module => {
        const modalRef = this.modalService.open(module.ConfirmationDialogComponent);
        const componentInstance = modalRef.componentInstance;

        componentInstance.title = this.translateService.instant("Save annotations");
        componentInstance.message = this.translateService.instant(
          "Annotations will display on your image on mouse-hover, overriding any other mouse-hover settings. Do you want to proceed?"
        );
        componentInstance.showAreYouSure = false;
        componentInstance.confirmLabel = this.translateService.instant("Yes, save annotations");

        modalRef.result.then(
          () => {
            // User confirmed, proceed with saving
            this._performSaveAnnotations();
          },
          () => {
            // User cancelled, do nothing
          }
        );
      });
    }
  }

  /**
   * Get the appropriate shape handler for an annotation
   * @param annotation The annotation to get a handler for
   * @returns A ShapeHandler implementation for the annotation's shape type
   */
  getShapeHandler(annotation: any): ShapeHandler {
    // For rectangle shape types, use rectangle handler
    if (this.isRectangle(annotation)) {
      return ShapeHandlerFactory.getHandler(AnnotationShapeType.RECTANGLE);
    }

    // For circle shape types, use circle handler
    if (this.isCircle(annotation)) {
      return ShapeHandlerFactory.getHandler(AnnotationShapeType.CIRCLE);
    }

    // For text shape types, use text handler
    if (this.isText(annotation)) {
      return ShapeHandlerFactory.getHandler(AnnotationShapeType.TEXT);
    }

    // For link shape types, use link handler
    if (this.isLink(annotation)) {
      return ShapeHandlerFactory.getHandler(AnnotationShapeType.LINK);
    }

    // Default to rectangle handler
    return ShapeHandlerFactory.getHandler(AnnotationShapeType.RECTANGLE);
  }

  /**
   * Handle click on annotation in read-only mode
   * This will trigger opening the full screen viewer with annotations enabled
   */
  onReadOnlyAnnotationClick(event: MouseEvent): void {
    if (this.readOnly && this.isRegularView) {
      event.preventDefault();
      event.stopPropagation();

      // Emit the click event to be handled by the parent component
      this.readOnlyAnnotationClick.emit(event);
    }
  }

  onLinkAnnotationClick(event: MouseEvent, annotation: any): void {
    // Handle clicks on any annotation that has a linkUrl
    if (annotation.linkUrl) {
      event.preventDefault();
      event.stopPropagation();

      // Open the link in a new tab only in browser environment
      if (isPlatformBrowser(this.platformId)) {
        window.open(annotation.linkUrl, "_blank", "noopener,noreferrer");
      }
    }
  }

  /**
   * Check if the tutorial should be started and initialize it if needed
   * This is extracted to a method so it can be called from multiple places
   */
  private _maybeStartAnnotationTutorial(): void {
    // Start the tutorial if the user hasn't completed it yet
    this.utilsService.delay(700).subscribe(() => {
      void import("@shared/constants/tutorial.constants").then(({ TUTORIAL_IDS }) => {
        if (!this.tutorialService.hasTutorialBeenCompleted(TUTORIAL_IDS.ANNOTATION_TOOL_TOOLBAR)) {
          const translatedTutorial = this.tutorialFactoryService.createTutorial("ANNOTATION_TOOL_TOOLBAR");
          this.tutorialService.startTutorial(translatedTutorial);
          this._tutorialActive = true;
          this.cdRef.markForCheck();

          // Set up listener for when tutorial completes
          const tutorialSub = this.tutorialService.activeTutorial$.subscribe(tutorial => {
            if (!tutorial && this._tutorialActive) {
              // Tutorial completed, clean up
              tutorialSub.unsubscribe();
              this._tutorialActive = false;
            }
          });
        }
      });
    });
  }

  /**
   * Initialize all event streams in one place for better maintainability
   * @private
   */
  private _initializeEventStreams(): void {
    if (!this.isBrowser) {
      return;
    }

    // Clean up any existing subscriptions first to prevent duplicates
    this._cleanupEventSubscriptions();

    // Set up window resize events (can be passive as we don't prevent default)
    const windowResize$ = fromEvent<UIEvent>(window, "resize", { passive: true }).pipe(takeUntil(this.destroyed$));

    // Set up immediate window resize event (for setting flag)
    const windowResizeImmediate$ = fromEvent<UIEvent>(window, "resize", { passive: true }).pipe(
      takeUntil(this.destroyed$)
    );

    // Set up window scroll event (already correctly using passive: true)
    const windowScroll$ = fromEvent<Event>(window, "scroll", { passive: true }).pipe(takeUntil(this.destroyed$));

    // Set up pointer events for drag operations
    const documentPointerMove$ = fromEvent<PointerEvent>(document, "pointermove", { passive: false }).pipe(
      takeUntil(this.destroyed$)
    );

    const documentPointerUp$ = fromEvent<PointerEvent>(document, "pointerup", { passive: false }).pipe(
      takeUntil(this.destroyed$)
    );

    const documentPointerCancel$ = fromEvent<PointerEvent>(document, "pointercancel", { passive: false }).pipe(
      takeUntil(this.destroyed$)
    );

    // Add subscriptions to our tracking array

    // Immediate resize handling (set flag) - no debounce needed
    this._eventSubscriptions.push(
      windowResizeImmediate$.subscribe(() => {
        this.isResizing = true;
        this.cdRef.markForCheck();
      })
    );

    // Debounced resize handling - apply debounce here
    this._eventSubscriptions.push(
      windowResize$
        .pipe(debounceTime(this.RESIZE_DEBOUNCE_MS)) // Apply debounce here rather than on the stream creation
        .subscribe(event => {
          this.handleWindowResize(event);
          this.isResizing = false;
          this.cdRef.markForCheck();
        })
    );

    // Window scroll handling for container sizing
    this._eventSubscriptions.push(
      windowScroll$.subscribe(() => {
        this._updateAnnotationContainerSize();
      })
    );

    // Pointer move handling for drag operations
    this._eventSubscriptions.push(
      documentPointerMove$
        .pipe(filter(() => this._dragInProgress && this.currentlyDragging !== null))
        .subscribe(event => {
          if (this._activePointerId !== null && event.pointerId === this._activePointerId) {
            this.dragMove(event);
          }
        })
    );

    // Pointer up handling to end drag
    this._eventSubscriptions.push(
      documentPointerUp$
        .pipe(filter(() => this._dragInProgress && this.currentlyDragging !== null))
        .subscribe(event => {
          if (this._activePointerId !== null && event.pointerId === this._activePointerId) {
            this.endDrag(event);
          }
        })
    );

    // Pointer cancel handling to end drag
    this._eventSubscriptions.push(
      documentPointerCancel$
        .pipe(filter(() => this._dragInProgress && this.currentlyDragging !== null))
        .subscribe(event => {
          if (this._activePointerId !== null && event.pointerId === this._activePointerId) {
            this.endDrag(event);
          }
        })
    );
  }

  /**
   * Clean up event subscriptions to prevent memory leaks
   * @private
   */
  private _cleanupEventSubscriptions(): void {
    // Unsubscribe from all event subscriptions
    this._eventSubscriptions.forEach(subscription => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });

    // Clear the array
    this._eventSubscriptions = [];
  }

  /**
   * Set up ResizeObserver for the image element
   * @private
   */
  private _setupResizeObserver(): void {
    // Clean up any existing ResizeObserver first
    this._cleanupResizeObserver();

    // Verify we have a valid DOM Element for the ResizeObserver
    if (
      this.imageElement &&
      this.imageElement.nodeType === Node.ELEMENT_NODE &&
      typeof this.imageElement.getBoundingClientRect === "function"
    ) {
      try {
        // Create and store the observer instance
        this._resizeObserver = new ResizeObserver(() => {
          // Update the annotation container size when the image resizes
          this._updateAnnotationContainerSize();
        });

        // Observe the image element
        this._resizeObserver.observe(this.imageElement);
      } catch (error) {
        console.warn("Failed to set up ResizeObserver:", error);
        this._resizeObserver = null;
      }
    } else {
      console.warn("Cannot set up ResizeObserver: Invalid image element");
    }
  }

  /**
   * Clean up the ResizeObserver to prevent memory leaks
   * @private
   */
  private _cleanupResizeObserver(): void {
    if (this._resizeObserver) {
      try {
        this._resizeObserver.disconnect();
      } catch (error) {
        console.warn("Error disconnecting ResizeObserver:", error);
      }
      this._resizeObserver = null;
    }
  }

  /**
   * Helper methods for HTML-based annotation positioning
   */

  /**
   * Get container dimensions for shape calculations
   * @returns Object with containerWidth and containerHeight
   * @private
   */
  private _getContainerDimensions(): { containerWidth: number; containerHeight: number } {
    // Get container dimensions to calculate percentages
    const containerElement = document.querySelector(".annotation-container") as HTMLElement;
    let containerWidth = 1000; // Fallback width
    let containerHeight = 1000; // Fallback height

    if (containerElement) {
      const rect = containerElement.getBoundingClientRect();
      containerWidth = rect.width || 1000;
      containerHeight = rect.height || 1000;
    } else if (this.imageElement) {
      // Fallback to the image element if container not found
      const rect = this.imageElement.getBoundingClientRect();
      containerWidth = rect.width || 1000;
      containerHeight = rect.height || 1000;
    }

    return { containerWidth, containerHeight };
  }

  /**
   * Prepare a shape annotation and open the form
   * @param shapeType The type of shape to create ("rectangle", "circle", or "text")
   * @private
   */
  private _prepareShape(shapeType: string): void {
    // Debounce to prevent duplicate actions
    const now = Date.now();
    if (now - this._lastActionTime < this.ACTION_DEBOUNCE_MS) {
      return;
    }
    this._lastActionTime = now;

    // Ensure annotation container size is up to date
    this._updateAnnotationContainerSize();

    // Get container dimensions
    const { containerWidth, containerHeight } = this._getContainerDimensions();

    // Prepare the shape data based on type
    let shapeData: ShapeData;

    if (shapeType === AnnotationShapeType.RECTANGLE) {
      // Calculate how much 100px is as a percentage of container dimensions
      const widthPercent = (100 / containerWidth) * 100;
      const heightPercent = (100 / containerHeight) * 100;

      shapeData = {
        type: AnnotationShapeType.RECTANGLE,
        color: this.annotationService.getDefaultColor(),
        // Rectangle coordinates (centered with 100x100px equivalent size)
        x: 50 - widthPercent / 2,
        y: 50 - heightPercent / 2,
        width: widthPercent,
        height: heightPercent
      };
    } else if (shapeType === AnnotationShapeType.CIRCLE) {
      // circle
      // Calculate radius in percentage (use the smaller dimension for a perfect circle)
      // 50px radius = 100px diameter
      const radiusInPixels = 50;
      const smallerDimension = Math.min(containerWidth, containerHeight);
      const radiusPercent = (radiusInPixels / smallerDimension) * 100;

      shapeData = {
        type: AnnotationShapeType.CIRCLE,
        color: this.annotationService.getDefaultColor(),
        // Circle coordinates (centered with 100px diameter)
        cx: 50,
        cy: 50,
        r: radiusPercent
      };
    } else if (shapeType === AnnotationShapeType.TEXT) {
      // text
      // For text, we'll use a much smaller box that will expand with content
      const widthPercent = 10; // Smaller initial width for text
      const heightPercent = 5; // Smaller initial height for text

      // Adjust height percentage based on image scale factor
      const adjustedHeightPercent = heightPercent * (1 / this.imageScaleFactor);

      shapeData = {
        type: AnnotationShapeType.TEXT,
        color: "#000000", // Black text for annotations
        // Text coordinates (centered)
        x: 50 - widthPercent / 2,
        y: 50 - adjustedHeightPercent / 2,
        width: widthPercent,
        height: adjustedHeightPercent
      };
    } else if (shapeType === AnnotationShapeType.LINK) {
      // link
      // For links, we'll use a similar size to text annotations
      const widthPercent = 15; // Slightly wider for link display
      const heightPercent = 6; // Reasonable height for link button

      // Adjust height percentage based on image scale factor
      const adjustedHeightPercent = heightPercent * (1 / this.imageScaleFactor);

      shapeData = {
        type: AnnotationShapeType.LINK,
        color: this.annotationService.getDefaultColor(),
        // Link coordinates (centered)
        x: 50 - widthPercent / 2,
        y: 50 - adjustedHeightPercent / 2,
        width: widthPercent,
        height: adjustedHeightPercent
      };
    }

    // Open the form to collect title and description before creating the shape
    this.openAnnotationForm(shapeData);
  }

  /**
   * Private method to actually perform the save after confirmation
   */
  private _performSaveAnnotations(): void {
    // Set saving state
    this.savingAnnotations = true;
    this.saveSuccess = false;
    this.cdRef.markForCheck();

    const annotationsJson = JSON.stringify(this.annotations);

    // Choose the appropriate method based on whether we're dealing with a revision or an image
    let saveMethod;
    if (this.revision.hasOwnProperty("label")) {
      saveMethod = this.imageApiService.setRevisionAnnotations(this.revision.pk, annotationsJson);
    } else {
      saveMethod = this.imageApiService.setAnnotations(this.revision.pk, annotationsJson);
    }

    // Now use the selected method with a single set of success/error handlers
    saveMethod.pipe(take(1)).subscribe({
      next: () => {
        // Show success indicator
        this.savingAnnotations = false;
        this.saveSuccess = true;
        this.cdRef.markForCheck();

        // Update the annotations in the store
        void import("@app/store/actions/image.actions").then(actions => {
          // Check if this is an image revision or an image
          const isRevision = "image" in this.revision;
          const imagePk = isRevision ? (this.revision as ImageRevisionInterface).image : this.revision.pk;
          const revisionLabel = isRevision ? (this.revision as ImageRevisionInterface).label : null;

          this.store$.dispatch(
            new actions.UpdateAnnotations({
              pk: imagePk,
              revisionLabel: revisionLabel,
              annotations: annotationsJson
            })
          );
        });

        // Emit event to notify parent component that annotations were saved
        this.annotationsSaved.emit(annotationsJson);

        // Reset success indicator after 1.5 seconds
        this.utilsService.delay(1500).subscribe(() => {
          this.saveSuccess = false;
          this.cdRef.markForCheck();
        });
      },
      error: err => {
        // Show error in popup
        console.error("Error saving annotations:", err);
        this.savingAnnotations = false;
        this.cdRef.markForCheck();

        this.popNotificationsService.error(
          this.translateService.instant("Failed to save annotations: {{error}}", {
            error: err.message || this.translateService.instant("Unknown error")
          })
        );
      }
    });
  }

  /**
   * Update the annotation container size to match the actual displayed image
   */
  private _updateAnnotationContainerSize(): void {
    // Make sure we have a valid image element
    if (!this.imageElement) {
      console.warn("No image element provided to annotation tool");
      return;
    }

    // Verify the element is a valid DOM element with getBoundingClientRect method
    if (
      this.imageElement.nodeType !== Node.ELEMENT_NODE ||
      typeof this.imageElement.getBoundingClientRect !== "function"
    ) {
      console.warn("Image element is not a valid DOM element or doesn't support getBoundingClientRect");
      return;
    }

    // Get the bounds of the image itself
    const imageBounds = this.imageElement.getBoundingClientRect();

    // Skip if image element has no dimensions yet
    if (imageBounds.width === 0 || imageBounds.height === 0) {
      console.warn("Image has zero dimensions, unable to size annotation container");
      return;
    }

    // Find the annotation container within this component
    const annotationContainer = this.elementRef.nativeElement.querySelector(".annotation-container") as HTMLElement;
    if (!annotationContainer) {
      console.warn("Annotation container not found in this component");
      return;
    }

    // Find the annotation layer within this component
    const annotationLayer = this.elementRef.nativeElement.querySelector(".annotation-layer") as HTMLElement;
    if (!annotationLayer) {
      console.warn("Annotation layer not found in this component");
      return;
    }

    // Check if mobile menu exists and get its height
    let mobileMenuHeight = 0;

    // Only access document in browser environment
    if (isPlatformBrowser(this.platformId) && this.isRegularView) {
      const mobileMenu = document.querySelector("astrobin-mobile-menu");
      if (mobileMenu) {
        mobileMenuHeight = mobileMenu.clientHeight || 0;
      }
    }

    annotationContainer.style.position = "absolute";
    annotationContainer.style.width = `${imageBounds.width}px`;
    annotationContainer.style.height = `${imageBounds.height}px`;
    annotationContainer.style.top = `${imageBounds.top - mobileMenuHeight}px`;
    annotationContainer.style.left = `${imageBounds.left}px`;

    // Store these dimensions for percentage calculations
    this.containerWidth = imageBounds.width;
    this.containerHeight = imageBounds.height;

    // Calculate scale factor for text annotations based on image's actual rendered size vs. natural size
    if (this.naturalWidth && this.naturalHeight) {
      // Use the smaller ratio to ensure text isn't too large on extreme aspect ratios
      const widthRatio = imageBounds.width / this.naturalWidth;
      const heightRatio = imageBounds.height / this.naturalHeight;
      this.imageScaleFactor = Math.min(widthRatio, heightRatio);

      // Constrain the scale factor to avoid extremely small text
      this.imageScaleFactor = Math.max(0.3, Math.min(1.5, this.imageScaleFactor));
    } else {
      // Default scale factor if natural dimensions are not available
      this.imageScaleFactor = 1;
    }

    // Position the layer to fill the container
    annotationLayer.style.width = "100%";
    annotationLayer.style.height = "100%";
    annotationLayer.style.top = "0";
    annotationLayer.style.left = "0";
  }

  /**
   * Clear all annotations (internal method without confirmation)
   */
  private _clearAll(): void {
    this.annotationService.clearAllAnnotations();
    this.cdRef.detectChanges();
  }

  /**
   * Create annotation from pending data and form result using the annotation service
   */
  private _createAnnotationFromPendingData(formData: any): void {
    if (!this.pendingShapeData) {
      console.warn("No pending shape data found");
      return;
    }

    // Use the service's dedicated method to create the annotation with all properties
    this.annotationService.createAnnotationFromPendingData(this.pendingShapeData, formData);

    // Reset pending data
    this.pendingShapeData = null;

    // Force UI update
    this.cdRef.detectChanges();
  }

  /**
   * Set up an event listener for image load to update container size when image loads/changes
   */
  private _setupImageLoadListener(): void {
    // Clear any existing subscription
    if (this._imageLoadSubscription) {
      this._imageLoadSubscription.unsubscribe();
      this._imageLoadSubscription = null;
    }

    // Use RxJS fromEvent for the image load event if it's an HTMLImageElement
    if (
      this.isBrowser &&
      this.imageElement instanceof HTMLImageElement &&
      typeof this.imageElement.addEventListener === "function"
    ) {
      this._imageLoadSubscription = fromEvent(this.imageElement, "load")
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          // Update natural dimensions from the newly loaded image if they aren't provided
          if (!this.naturalWidth && this.imageElement instanceof HTMLImageElement) {
            this.naturalWidth = this.imageElement.naturalWidth;
            this.naturalHeight = this.imageElement.naturalHeight;
          }

          // Update annotation container size after a short delay to ensure image is rendered
          this.utilsService.delay(100).subscribe(() => {
            this._updateAnnotationContainerSize();
          });
        });
    }
  }

  /**
   * Initialize message form fields using ngx-formly
   */
  private _initMessageFormFields(readOnly: boolean = false): void {
    // Determine appropriate placeholders based on shape type
    const titlePlaceholder =
      this.pendingShapeData?.type === AnnotationShapeType.TEXT
        ? this.translateService.instant("Title (required)")
        : this.translateService.instant("Title (optional)");

    // Build the fields array conditionally
    const fields: any[] = [
      {
        key: "title",
        type: "input",
        wrappers: ["default-wrapper"],
        props: {
          required: this.pendingShapeData?.type === AnnotationShapeType.TEXT,
          label: "", // Remove label
          placeholder: titlePlaceholder,
          maxLength: 50,
          disabled: readOnly // Disable input in read-only mode
        },
        // Add validators for required field
        expressions: {
          "props.required": () => this.pendingShapeData?.type === AnnotationShapeType.TEXT,
          "props.placeholder": () =>
            this.pendingShapeData?.type === AnnotationShapeType.TEXT
              ? this.translateService.instant("Title (required)")
              : this.translateService.instant("Title (optional)")
        }
      }
    ];

    // Add message field ONLY for non-link annotations
    if (this.pendingShapeData?.type !== AnnotationShapeType.LINK) {
      fields.push({
        key: "message",
        type: "textarea",
        wrappers: ["default-wrapper"],
        props: {
          label: "", // Remove label
          placeholder: this.translateService.instant("Description (optional)"),
          rows: 5,
          maxLength: 1000,
          disabled: readOnly // Disable textarea in read-only mode
        }
      });
    }

    // Add linkUrl field for link, circle, and rectangle annotations (but not text)
    if (
      this.pendingShapeData?.type === AnnotationShapeType.LINK ||
      this.pendingShapeData?.type === AnnotationShapeType.CIRCLE ||
      this.pendingShapeData?.type === AnnotationShapeType.RECTANGLE
    ) {
      fields.push({
        key: "linkUrl",
        type: "input",
        wrappers: ["default-wrapper"],
        props: {
          required: this.pendingShapeData?.type === AnnotationShapeType.LINK, // Only required for link type
          label: "",
          placeholder:
            this.pendingShapeData?.type === AnnotationShapeType.LINK
              ? this.translateService.instant("URL (e.g., https://example.com)")
              : this.translateService.instant("Optional URL (e.g., https://example.com)"),
          type: "text",
          disabled: readOnly,
          pattern: "^https?://.*"
        },
        validation: {
          messages: {
            required: this.translateService.instant("URL is required"),
            pattern: this.translateService.instant("URL must start with http:// or https://")
          }
        }
      });
    }

    // Add color and opacity fields for all types
    fields.push(
      {
        key: "color",
        type: readOnly ? "input" : "color-picker", // In read-only mode, don't use color-picker
        wrappers: ["default-wrapper"],
        props: {
          label: "", // Remove label
          colors: this.colors,
          required: true,
          hideRequiredMarker: true,
          disabled: readOnly, // Disable color picker in read-only mode
          hide: readOnly // Hide color picker in read-only mode
        }
      },
      {
        key: "opacity",
        type: "slider",
        wrappers: ["default-wrapper"],
        defaultValue: 0.8,
        props: {
          label: this.translateService.instant("Opacity"),
          required: true,
          hideRequiredMarker: true,
          disabled: readOnly,
          hide: readOnly,
          singleValue: true, // Enable single value mode
          sliderOptions: {
            floor: 0.1,
            ceil: 1.0,
            step: 0.1,
            showTicks: true,
            showTicksValues: false,
            animate: false
          }
        }
      }
    );

    this.messageFields = fields;
  }

  /**
   * Load saved annotations from the revision object
   */
  private _loadSavedAnnotations(): void {
    // Check if we have URL annotations - if yes, don't load from saved annotations
    if (this.hasUrlAnnotations) {
      return;
    }

    if (!this.revision) {
      return;
    }

    // Clear existing annotations before loading new ones
    this.annotations = [];
    this.annotationService.clearAllAnnotations();

    // Use the service method to load annotations from the revision
    this.annotationService
      .loadSavedAnnotations(this.revision)
      .then(() => {
        this._updateAnnotationContainerSize();
        this.cdRef.markForCheck();
      })
      .catch(error => {
        // Handle error loading annotations
        console.error("Error loading annotations from revision:", error);
        this.annotations = [];
        this.annotationService.clearAllAnnotations();
        this.cdRef.markForCheck();
      });
  }
}
